import React, { useState, useEffect } from 'react';
import { RealtimeDBService } from '../firebase/unifiedDataService';
import ViewLayout2 from '../components/ui/ViewLayout2';
import { Card, Button } from '../components/ui/BaseComponents';
import { Logger } from '../utils/logger';
import { notifyError, notifySuccess, notifyWarning, notifyConfirm } from '../services/notificationService';

const log = Logger.create('DatabaseManager');

function DatabaseManagerView({ currentSessionId }) {
  // State variables
  const [allSessions, setAllSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteAnswersConfirm, setDeleteAnswersConfirm] = useState(null);
  const [loadingSessionId, setLoadingSessionId] = useState(null);
  const [copySuccess, setCopySuccess] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);

  // Fetch all sessions on component mount
  useEffect(() => {
    fetchAllSessions();
  }, []);

  // Fetch all sessions from database
  const fetchAllSessions = async () => {
    setSessionsLoading(true);
    
    try {
      const sessionsData = await RealtimeDBService.get('sessions');
      if (sessionsData) {
        const sessionsArray = Object.keys(sessionsData).map(key => {
          let firstQuestionText = "Keine Fragen";
          let createdAt = "Unbekannt";
          let createdAtTimestamp = sessionsData[key].createdAt || 0;
          let answerCount = 0;
          
          if (sessionsData[key].questions && sessionsData[key].questions.length > 0) {
            firstQuestionText = sessionsData[key].questions[0].question;
          }
          
          if (sessionsData[key].createdAt) {
            createdAt = new Date(sessionsData[key].createdAt).toLocaleDateString();
          }
          
          // Count total answers across all questions
          if (sessionsData[key].answers) {
            const answers = sessionsData[key].answers;
            Object.keys(answers).forEach(questionIndex => {
              answerCount += Object.keys(answers[questionIndex] || {}).length;
            });
          }
          
          return {
            id: key,
            questionCount: sessionsData[key].questions?.length || 0,
            currentQuestion: sessionsData[key].currentQuestion || 0,
            firstQuestion: firstQuestionText.substring(0, 30) + (firstQuestionText.length > 30 ? "..." : ""),
            createdAt: createdAt,
            createdAtTimestamp: createdAtTimestamp,
            answerCount: answerCount
          };
        });
        
        // Apply default sorting
        sortData(sessionsArray, sortConfig.key, sortConfig.direction);
        setAllSessions(sessionsArray);
      } else {
        setAllSessions([]);
      }
    } catch (error) {
      log.error('Error fetching sessions:', error);
    } finally {
      setSessionsLoading(false);
    }
  };

  // Sort data based on column and direction
  const sortData = (data, key, direction) => {
    return data.sort((a, b) => {
      if (key === 'createdAtTimestamp') {
        return direction === 'asc' ? a[key] - b[key] : b[key] - a[key];
      }
      
      if (typeof a[key] === 'number' && typeof b[key] === 'number') {
        return direction === 'asc' ? a[key] - b[key] : b[key] - a[key];
      }
      
      if (a[key] < b[key]) {
        return direction === 'asc' ? -1 : 1;
      }
      if (a[key] > b[key]) {
        return direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }

  // Handle column sorting
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    
    setSortConfig({ key, direction });
    
    const sortedData = [...allSessions];
    sortData(sortedData, key, direction);
    setAllSessions(sortedData);
  };

  // Get sort icon for column headers
  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) {
      return '⇅'; // Up-down arrow
    }
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  // Delete a session
  const deleteSession = async (id) => {
    if (id === currentSessionId) {
      notifyWarning('Du kannst die aktuelle Session nicht löschen!');
      setDeleteConfirm(null);
      return;
    }
    
    try {
      await RealtimeDBService.remove(`sessions/${id}`);
      // Refresh the list
      fetchAllSessions();
      setDeleteConfirm(null);
    } catch (error) {
      log.error('Error deleting session:', error);
      notifyError('Fehler beim Löschen der Session');
    }
  };

  // Delete all answers for a session
  const deleteSessionAnswers = async (id) => {
    try {
      await RealtimeDBService.update(`sessions/${id}`, {
        answers: {}
      });
      notifySuccess(`Antworten für Session ${id} erfolgreich gelöscht`);
      fetchAllSessions();
      setDeleteAnswersConfirm(null);
    } catch (error) {
      log.error('Error deleting session answers:', error);
      notifyError('Fehler beim Löschen der Antworten');
    }
  };

  // Delete all sessions except current
  const deleteAllSessions = async () => {
    const confirmed = await notifyConfirm(
      'Möchtest du wirklich ALLE Sessions außer der aktuellen löschen?',
      { title: 'Alle Sessions löschen', variant: 'danger', confirmLabel: 'Ja, alle löschen' }
    );
    if (!confirmed) {
      setConfirmDeleteAll(false);
      return;
    }
    
    setSessionsLoading(true);
    try {
      const promises = allSessions
        .filter(session => session.id !== currentSessionId)
        .map(session => RealtimeDBService.remove(`sessions/${session.id}`));
      
      await Promise.all(promises);
      notifySuccess('Alle Sessions wurden erfolgreich gelöscht');
      fetchAllSessions();
    } catch (error) {
      log.error('Error deleting all sessions:', error);
      notifyError('Fehler beim Löschen aller Sessions');
    } finally {
      setSessionsLoading(false);
      setConfirmDeleteAll(false);
    }
  };

  // Delete all answers from all sessions
  const deleteAllAnswers = async () => {
    const confirmed = await notifyConfirm(
      'Möchtest du wirklich ALLE Antworten aus allen Sessions löschen?',
      { title: 'Alle Antworten löschen', variant: 'danger', confirmLabel: 'Ja, alle löschen' }
    );
    if (!confirmed) {
      return;
    }
    
    setSessionsLoading(true);
    try {
      const promises = allSessions.map(session => 
        RealtimeDBService.update(`sessions/${session.id}`, { answers: {} })
      );
      
      await Promise.all(promises);
      notifySuccess('Alle Antworten wurden erfolgreich gelöscht');
      fetchAllSessions();
    } catch (error) {
      log.error('Error deleting all answers:', error);
      notifyError('Fehler beim Löschen aller Antworten');
    } finally {
      setSessionsLoading(false);
    }
  };

  // Load a different quiz session
  const loadSession = (id) => {
    if (id === currentSessionId) {
      // Already on this session
      return;
    }
    
    setLoadingSessionId(id);
    
    // Navigate to the selected quiz session's live mode view
    // Firebase Auth is handled globally, no localStorage needed
    window.location.href = `/live/${id}`;
  };

  // Open quiz participant view
  const openQuizView = (id) => {
    window.open(`/play/${id}`, '_blank');
  };

  // Copy quiz link to clipboard
  const copyQuizLink = (id) => {
    const baseUrl = window.location.origin;
    const quizUrl = `${baseUrl}/play/${id}`;
    
    navigator.clipboard.writeText(quizUrl)
      .then(() => {
        setCopySuccess(id);
        setTimeout(() => setCopySuccess(null), 2000);
      })
      .catch(() => {
        notifyError('Fehler beim Kopieren des Links');
      });
  };

  return (
    <ViewLayout2
      title="Datenbank-Manager"
      onBack="/admin/dashboard"
      maxWidth="7xl"
      headerActions={
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAllSessions}
          >
            Aktualisieren
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={deleteAllAnswers}
          >
            Alle Antworten löschen
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setConfirmDeleteAll(true)}
          >
            Alle Sessions löschen
          </Button>
        </div>
      }
    >
      <Card>

      {confirmDeleteAll && (
        <div className="bg-destructive/10 dark:bg-destructive/20 border border-destructive rounded-lg p-4 mb-4">
          <p className="text-destructive font-medium text-sm mb-3 flex items-center">
            <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Diese Aktion kann nicht rückgängig gemacht werden!
          </p>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmDeleteAll(false)}
            >
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={deleteAllSessions}
            >
              Löschen bestätigen
            </Button>
          </div>
        </div>
      )}
      
      {sessionsLoading ? (
        <p className="text-center py-8 text-muted-foreground">Lade Sitzungen...</p>
      ) : allSessions.length === 0 ? (
        <p className="text-center py-8 text-muted-foreground">Keine Sitzungen gefunden</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border">
              <tr className="bg-muted/50">
                <th className="text-left py-3 px-4 cursor-pointer hover:bg-muted theme-transition" onClick={() => requestSort('id')}>
                  Session-ID {getSortIcon('id')}
                </th>
                <th className="text-left py-3 px-4 cursor-pointer hover:bg-muted theme-transition" onClick={() => requestSort('createdAtTimestamp')}>
                  Erstellt am {getSortIcon('createdAtTimestamp')}
                </th>
                <th className="text-left py-3 px-4 cursor-pointer hover:bg-muted theme-transition" onClick={() => requestSort('firstQuestion')}>
                  Erste Frage {getSortIcon('firstQuestion')}
                </th>
                <th className="text-center py-3 px-4 cursor-pointer hover:bg-muted theme-transition" onClick={() => requestSort('questionCount')}>
                  Fragen {getSortIcon('questionCount')}
                </th>
                <th className="text-center py-3 px-4 cursor-pointer hover:bg-muted theme-transition" onClick={() => requestSort('answerCount')}>
                  Antworten {getSortIcon('answerCount')}
                </th>
                <th className="text-center py-3 px-4">
                  Aktionen
                </th>
              </tr>
            </thead>
            <tbody>
              {allSessions.map(session => (
                <tr 
                  key={session.id} 
                  className={`border-b border-border theme-transition ${
                    session.id === currentSessionId 
                      ? "bg-primary/10 dark:bg-primary/20" 
                      : "hover:bg-muted/50"
                  }`}
                >
                  <td className="py-3 px-4">
                    <span className={session.id === currentSessionId ? "font-semibold text-primary" : ""}>
                      {session.id}
                      {session.id === currentSessionId && " (aktuell)"}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-xs text-muted-foreground">
                    {session.createdAt}
                  </td>
                  <td className="py-3 px-4 text-xs text-muted-foreground">
                    {session.firstQuestion}
                  </td>
                  <td className="text-center py-3 px-4">{session.questionCount}</td>
                  <td className="text-center py-3 px-4">{session.answerCount}</td>
                  <td className="py-3 px-4">
                    <div className="flex flex-wrap gap-2 justify-center">
                      {deleteConfirm === session.id ? (
                        <>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => deleteSession(session.id)}
                          >
                            Löschen
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeleteConfirm(null)}
                          >
                            Abbrechen
                          </Button>
                        </>
                      ) : deleteAnswersConfirm === session.id ? (
                        <>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => deleteSessionAnswers(session.id)}
                          >
                            Bestätigen
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeleteAnswersConfirm(null)}
                          >
                            Abbrechen
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => loadSession(session.id)}
                            disabled={loadingSessionId === session.id || session.id === currentSessionId}
                            title={session.id === currentSessionId ? "Aktuelle Session" : "Admin-Ansicht öffnen"}
                          >
                            {loadingSessionId === session.id ? "Lade..." : "Admin"}
                          </Button>
                          
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => openQuizView(session.id)}
                          >
                            Quiz
                          </Button>
                          
                          <Button
                            variant={copySuccess === session.id ? "default" : "outline"}
                            size="sm"
                            onClick={() => copyQuizLink(session.id)}
                          >
                            {copySuccess === session.id ? "✓ Kopiert!" : "Link"}
                          </Button>
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeleteAnswersConfirm(session.id)}
                          >
                            Reset
                          </Button>
                          
                          {session.id !== currentSessionId && (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => setDeleteConfirm(session.id)}
                            >
                              Löschen
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      <div className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground space-y-1">
        <p>Hier können Quiz-Sitzungen verwaltet werden. Die aktuelle Sitzung kann nicht gelöscht werden.</p>
        <p><strong>Admin</strong>: Admin-Ansicht öffnen | <strong>Quiz</strong>: Quiz-Ansicht öffnen | <strong>Link</strong>: Quiz-Link kopieren | <strong>Reset</strong>: Alle Antworten löschen | <strong>Löschen</strong>: Session vollständig löschen</p>
      </div>
    </Card>
    </ViewLayout2>
  );
}

export default DatabaseManagerView;